import { memo, useMemo, useState } from 'react';
import { Badge } from '../../../atoms/Badge/Badge';
import { Button } from '../../../atoms/Button/Button';
import { Icon } from '../../../atoms/Icon/Icon';
import { PlatformIcon } from '../../../atoms/PlatformIcon/PlatformIcon';
import { platformFor } from '@deepdive-icons';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { Search } from '../../../molecules/Search/Search';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import type { TableHeaderSort } from '../../../molecules/TableHeaderLabel/TableHeaderLabel';
import { Table, type TableColumn, type TableRowData } from '../../../organisms/Table/Table';
import { spanClass, type WidgetBlockProps } from '../../widgets/shared/cardShell';
import {
  SENTIMENT_BADGE,
  pageWindow,
  tableEmptyLabel,
  wholeFormat as whole,
} from '../shared/tableShared';
import { TableWidgetActions, tableDraggingClass } from '../TableWidgetActions/TableWidgetActions';

/**
 * Competitors — `Top performing content` (2199:56613, in 2030:19055). Also on
 * both Influencers detail pages and custom reports.
 *
 * Every post in the filtered set, across every selected brand, ranked by the
 * interactions it drew.
 *
 * **Client-side sort, search and page**, as in the prototype: the contract
 * returns every post in scope, already ranked, and this block orders it —
 * unless `paging` is given: then the view holds one page the server already
 * searched and sorted, the block draws it as is, and a search, sort or page
 * change goes back to the owner. Only the columns the server can order by
 * sort, and the badge counts the matching posts.
 *
 * **Recut on 2026-09-23** (frame 931:36991, instance 2199:56613 — it replaced
 * 2026:3442). The table went from six columns to ten: `Post summary` became
 * `Conversations`, `Total interactions` became `Engagement`, `Channel`,
 * `Intent`, `Topic`, `Reactions`, `Comments` and `Shares` arrived, and `Rank`
 * and `% of overall interactions` were dropped — both confirmed with the user.
 * Position now lives in the sort order alone; `rank` survives as the
 * tie-break behind every text sort, and `Tune sentiment` still shows it.
 *
 * Decisions the frame does not make:
 *
 *  1. **The badge counts every post in the period**, not the search results.
 *  2. **Search matches the conversation and the summary behind it**, as a
 *     case-insensitive substring, so "robi" or "network" narrow it even though
 *     neither word need appear in the copy on screen.
 *  3. **No loading variant** — see `tableEmptyLabel`.
 */

/** Figma draws ten body rows, which is also what its pagination implies. */
export const TOP_CONTENT_PAGE_SIZE = 10;
const PAGE_SIZE = TOP_CONTENT_PAGE_SIZE;

/** One post, labelled. */
export type TopPostView = {
  id: number;
  /** Position by interactions, 1-based — fixed to the post, not to the current sort. */
  rank: number;
  /** `Robi · Network coverage complaint on Facebook` — the line Tune sentiment names a post by. */
  summary: string;
  /** What the post says, for the `Conversations` column. */
  conversation: string;
  url: string;
  channel: string;
  channelLabel: string;
  intentLabel: string;
  topicLabel: string;
  /** Reactions **including likes**. */
  reactions: number;
  comments: number;
  shares: number;
  /** The sentiment the app counts — detected, or corrected if tuned. */
  sentiment: string;
  sentimentLabel: string;
  /** The sentiment's position in the chips' own order (positive, neutral, negative) — its sort key. */
  sentimentOrder: number;
  /** What the classifier said, whatever has been corrected since. */
  detected: string;
  detectedLabel: string;
  /** The saved Tune sentiment correction, or null. */
  corrected: string | null;
  interactions: number;
};

export type TopPerformingContentView = {
  /** Every post in scope, highest interactions first. */
  posts: TopPostView[];
};

export type TopContentSortKey =
  | 'conversation'
  | 'sentiment'
  | 'intent'
  | 'topic'
  | 'reactions'
  | 'comments'
  | 'shares'
  | 'interactions';
export type TopContentSort = { key: TopContentSortKey; direction: 'asc' | 'desc' };

/*
 * Opens on engagement, highest first — which is what the ranked list already
 * is and what the table is titled for.
 */
export const DEFAULT_TOP_CONTENT_SORT: TopContentSort = { key: 'interactions', direction: 'desc' };

/** Words sort A–Z; every figure opens highest first. */
const opensAscending = (key: TopContentSortKey) =>
  key === 'conversation' || key === 'sentiment' || key === 'intent' || key === 'topic';

/** The sort a header click moves to — the table's rule, shared with server-paged owners. */
export function nextTopContentSort(
  current: TopContentSort,
  key: TopContentSortKey,
): TopContentSort {
  return current.key === key
    ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { key, direction: opensAscending(key) ? 'asc' : 'desc' };
}

/** A server-paged table: the owner fetches; the block draws and reports changes. */
export type TopContentPaging = {
  /** 1-based, as served. */
  page: number;
  pageCount: number;
  /** Posts matching the scope and the search, across every page — the badge. */
  total: number;
  sort: TopContentSort;
  /** The columns the server can order by; the rest show no sort control. */
  sortable: readonly TopContentSortKey[];
  /** The search box's text (the owner debounces what it sends). */
  search: string;
  onPageChange: (page: number) => void;
  onSortChange: (sort: TopContentSort) => void;
  onSearchChange: (search: string) => void;
};

function compare(a: TopPostView, b: TopPostView, key: TopContentSortKey): number {
  switch (key) {
    case 'conversation':
      return a.conversation.localeCompare(b.conversation) || a.rank - b.rank;
    case 'intent':
      return a.intentLabel.localeCompare(b.intentLabel) || a.rank - b.rank;
    case 'topic':
      return a.topicLabel.localeCompare(b.topicLabel) || a.rank - b.rank;
    case 'sentiment':
      /* The chips' own order — positive, neutral, negative — then by rank within each. */
      return a.sentimentOrder - b.sentimentOrder || a.rank - b.rank;
    default:
      return a[key] - b[key];
  }
}

/** The table's rows for a search and a sort — exported for the parity test. */
export function filterTopContent(
  posts: readonly TopPostView[],
  query: string,
  sort: TopContentSort,
): TopPostView[] {
  const q = query.trim().toLowerCase();
  /*
   * Matches what the row *shows* — the conversation — plus the composed
   * summary behind it, so "robi" and "facebook" still narrow the list.
   */
  const filtered =
    q === ''
      ? posts
      : posts.filter(
          (post) =>
            post.conversation.toLowerCase().includes(q) || post.summary.toLowerCase().includes(q),
        );
  const sorted = [...filtered].sort((a, b) => compare(a, b, sort.key));
  return sort.direction === 'desc' ? sorted.reverse() : sorted;
}

export type TopPerformingContentTableProps = WidgetBlockProps & {
  view?: TopPerformingContentView;
  /** A row's `Breakdown ›` — opens the post breakdown drawer. */
  onBreakdown?: (post: TopPostView) => void;
  /**
   * Open the post itself — the row's own link. Figma draws the leading cell of
   * a hovered row in `text/link`; left unset, the rows are inert and the
   * leading cell stays plain.
   */
  onViewPost?: (post: TopPostView) => void;
  /**
   * Figma's `Tune sentiment` header button. **Left unset, the button is not
   * drawn** — Competitors is the only module with a `Tune sentiment` frame
   * (2037:7616); the Influencers instances carry the button only because they
   * were copied from it. Confirmed with the user 2026-09-23.
   */
  onTuneSentiment?: () => void;
  /** Server-paged mode. Omit for the client-side search, sort and page. */
  paging?: TopContentPaging;
};

export const TopPerformingContentTable = memo(function TopPerformingContentTable({
  status,
  view,
  span = 12,
  chrome,
  onBreakdown,
  onViewPost,
  onTuneSentiment,
  paging,
}: TopPerformingContentTableProps) {
  const [localSort, setLocalSort] = useState<TopContentSort>(DEFAULT_TOP_CONTENT_SORT);
  const [localPage, setLocalPage] = useState(1);
  const [localQuery, setLocalQuery] = useState('');

  const sort = paging?.sort ?? localSort;
  const query = paging?.search ?? localQuery;
  const posts = useMemo(() => (status === 'ready' && view ? view.posts : []), [status, view]);
  const shown = useMemo(
    () => (paging ? posts : filterTopContent(posts, localQuery, localSort)),
    [posts, localQuery, localSort, paging],
  );

  const pageCount = paging
    ? Math.max(1, paging.pageCount)
    : Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  /* Clamped on read rather than reset in an effect — Influencers' reason. */
  const currentPage = Math.min(paging?.page ?? localPage, pageCount);
  const visible = paging
    ? shown
    : shown.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const setPage = paging ? paging.onPageChange : setLocalPage;

  const sortFor = (key: TopContentSortKey): TableHeaderSort =>
    sort.key === key ? sort.direction : 'selector';

  /* A re-sort returns to page 1 — "page 4" meant something only in the order it was reached in. */
  const onSort = (key: TopContentSortKey) => () => {
    if (paging) {
      paging.onSortChange(nextTopContentSort(paging.sort, key));
      return;
    }
    setLocalPage(1);
    setLocalSort((prev) => nextTopContentSort(prev, key));
  };

  const sortable = (
    key: TopContentSortKey,
    header: string,
    width: string,
    extra?: Partial<TableColumn>,
  ): TableColumn =>
    paging && !paging.sortable.includes(key)
      ? { key, header, width, ...extra }
      : { key, header, width, sort: sortFor(key), onSort: onSort(key), ...extra };

  /*
   * Widths as a share of the frame's 1192. Eight are the file's own after the
   * 2026-09-23 recut — 70 / 99 / 97 / 103 / 81 / 113 / 112 — and `Intent` and
   * `Topic` are widened out of `Conversations` (the file sizes both for the
   * placeholder `12,975`; the real values are words):
   *
   *   Conversations 369 → 265   Intent 75 → 123   Topic 73 → 129
   *
   * Still 1192, and `Conversations` holds a sentence and truncates by design.
   */
  const columns: TableColumn[] = [
    sortable('conversation', 'Conversations', '22.2%'),
    /* The marks carry no text, so the column head is the only thing naming them. */
    { key: 'channel', header: 'Channel', cellType: 'actionIcons', width: '5.9%' },
    sortable('sentiment', 'Sentiment', '8.3%', { cellType: 'badge' }),
    sortable('intent', 'Intent', '10.3%'),
    sortable('topic', 'Topic', '10.8%'),
    sortable('reactions', 'Reactions', '8.1%'),
    sortable('comments', 'Comments', '8.6%'),
    sortable('shares', 'Shares', '6.8%'),
    sortable('interactions', 'Engagement', '9.5%'),
    /*
     * **No header.** The frame's last header cell holds no text: this column's
     * cells read `Breakdown ›` and a head repeating that names nothing. Each
     * cell's button carries the post in its accessible name.
     */
    { key: 'url', cellType: 'actionButtons', width: '9.4%' },
  ];

  const tableRows: TableRowData[] = visible.map((post) => ({
    key: String(post.id),
    /*
     * The whole row opens the post: `Table` hangs the action off the row and
     * gives the leading cell a button for the keyboard. Clicks on `Breakdown`
     * are excluded by `Table`'s own `opensRow` guard.
     */
    onActivate: onViewPost ? () => onViewPost(post) : undefined,
    activateLabel: onViewPost ? `Open post: ${post.conversation}` : undefined,
    cells: {
      conversation: { text: post.conversation },
      channel: {
        content: (() => {
          /* A live channel's key is an id, so its mark is found by its name. */
          const platform = platformFor(post.channel) ?? platformFor(post.channelLabel);
          return platform ? (
            <PlatformIcon name={platform.name} label={post.channelLabel} />
          ) : (
            /* A channel with no drawn mark falls back to its name, as ChartLegend does. */
            post.channelLabel
          );
        })(),
      },
      intent: { text: post.intentLabel },
      topic: { text: post.topicLabel },
      reactions: { text: whole.format(post.reactions) },
      comments: { text: whole.format(post.comments) },
      shares: { text: whole.format(post.shares) },
      interactions: { text: whole.format(post.interactions) },
      sentiment: {
        content: (
          <Badge type={SENTIMENT_BADGE[post.sentiment] ?? 'gray'}>{post.sentimentLabel}</Badge>
        ),
      },
      url: {
        content: (
          <Button
            hierarchy="ghostColor"
            iconTrailing={<Icon name="chevronRight" />}
            onClick={() => onBreakdown?.(post)}
            aria-label={`Breakdown of: ${post.conversation}`}
          >
            Breakdown
          </Button>
        ),
      },
    },
  }));

  const count = paging ? paging.total : posts.length;

  return (
    <Table
      className={[spanClass(span), tableDraggingClass(chrome)].filter(Boolean).join(' ')}
      label="Top performing content"
      columns={columns}
      rows={tableRows}
      /* A search can narrow the list to nothing; so can the filters. Say which. */
      emptyLabel={tableEmptyLabel(
        status,
        query.trim() === '' ? 'No posts in this period' : 'No posts match that search',
      )}
      header={
        <TableHeader
          flash={chrome?.flash}
          title="Top performing content"
          badge={`${whole.format(count)} total ${count === 1 ? 'post' : 'posts'}`}
          helpText="Every post about the selected brands, with what it was about and the engagement it drew."
          helpSupportingText="Opens on engagement, highest first. Reactions include likes, so Reactions, Comments and Shares add up to Engagement on every row. The search matches the conversation and the brand, topic and channel behind it."
          search={
            <Search
              aria-label="Search content"
              placeholder="Search content..."
              value={query}
              onValueChange={(next) => {
                if (paging) {
                  paging.onSearchChange(next);
                  return;
                }
                setLocalQuery(next);
                // A narrower list can be shorter than the page we are on.
                setLocalPage(1);
              }}
            />
          }
          actions={
            <>
              {/* Figma `Hierarchy=Secondary` with the leading `sentiment` glyph.
                  Drawn only where there is a modal to open — see the prop. */}
              {onTuneSentiment && (
                <Button
                  hierarchy="secondary"
                  iconLeading={<Icon name="sentiment" />}
                  onClick={onTuneSentiment}
                >
                  Tune sentiment
                </Button>
              )}
              <TableWidgetActions chrome={chrome} title="Top performing content" />
            </>
          }
        />
      }
      pagination={
        <Pagination
          page={currentPage}
          pageCount={pageCount}
          /* Five numbers, as the frame draws — see `pageWindow`. */
          pages={pageWindow(currentPage, pageCount)}
          onPageChange={setPage}
          label="Top content pages"
        />
      }
    />
  );
});
